import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Blocks, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/sonner';
import { AccessBadge } from '@/modules/back-testing/datasets/tools/tool-access';
import { ConfirmDeleteDialog } from '@/modules/back-testing/datasets/ui/confirm-delete-dialog';
import type { CustomTool } from '@/types/custom-tool.types';
import { NEW_CUSTOM_TOOL_PATH, customToolActionPath, customToolPath } from './paths';
import { useCustomTools, useDeleteCustomTool } from './use-custom-tools';
import { isWriteTool, useStepTools } from './use-step-tools';

interface CustomToolRowProps {
	tool: CustomTool;
	/** Some step changes data. */
	writes: boolean;
	onDelete: () => void;
}

const CustomToolRow = ({ tool, writes, onDelete }: CustomToolRowProps) => {
	const navigate = useNavigate();
	return (
		<li className='flex items-center gap-2 rounded-xl border border-gray-300 p-3'>
			<button type='button' onClick={() => navigate(customToolPath(tool.id))} className='flex min-w-0 flex-1 flex-col gap-1 text-left'>
				<span className='flex items-center gap-2'>
					<span className='truncate text-sm font-medium text-ink'>{tool.name}</span>
					{writes && <AccessBadge riskLevel='high' />}
				</span>
				<span className='flex items-center gap-1.5 text-xs text-gray-800'>
					<span className='truncate font-mono'>{tool.slug}</span>
					<span aria-hidden>·</span>
					<span className='shrink-0'>{`${tool.steps.length} steps`}</span>
				</span>
			</button>
			<Button variant='ghost' size='sm' className='h-7 text-xs font-normal text-gray-900' asChild>
				<Link to={customToolActionPath(tool.slug)}>Events and preconditions</Link>
			</Button>
			<Button variant='ghost' size='icon-sm' aria-label={`Edit ${tool.name}`} onClick={() => navigate(customToolPath(tool.id))}>
				<Pencil className='size-3.5' />
			</Button>
			<Button variant='ghost' size='icon-sm' aria-label={`Delete ${tool.name}`} onClick={onDelete}>
				<Trash2 className='size-3.5' />
			</Button>
		</li>
	);
};

const EmptyState = () => {
	const navigate = useNavigate();
	return (
		<div className='flex flex-col items-center gap-3 py-20 text-center'>
			<div className='flex size-10 items-center justify-center rounded-full bg-gray-100'>
				<Blocks className='size-5 text-gray-900' />
			</div>
			<div className='flex flex-col gap-1'>
				<p className='text-sm font-medium text-ink'>No custom tools yet</p>
				<p className='text-xs text-muted-foreground'>Combine two or more tools into one the AI can call.</p>
			</div>
			<Button variant='outline' className='h-8 rounded-full text-[13px] font-normal' onClick={() => navigate(NEW_CUSTOM_TOOL_PATH)}>
				New custom tool
			</Button>
		</div>
	);
};

/** The account's custom tools, each with what it combines and how it runs. */
export const CustomToolsList = () => {
	const { data: customTools, isLoading, isError } = useCustomTools();
	const { toolFor, isLoading: toolsLoading } = useStepTools();
	const remove = useDeleteCustomTool();
	const [deleting, setDeleting] = useState<CustomTool | null>(null);

	const confirmDelete = async () => {
		if (!deleting) return;
		try {
			await remove.mutateAsync(deleting.id);
			toast.success('Custom tool deleted');
		} catch {
			toast.error('Could not delete the custom tool');
		}
	};

	if (isLoading) return Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className='h-16 rounded-xl' />);
	if (isError) return <p className='py-10 text-center text-sm text-red-500'>Failed to load custom tools.</p>;
	if (!customTools?.length) return <EmptyState />;

	return (
		<>
			<ul className='flex flex-col gap-3'>
				{customTools.map((tool) => (
					<CustomToolRow
						key={tool.id}
						tool={tool}
						writes={!toolsLoading && tool.steps.some((step) => isWriteTool(toolFor(step)))}
						onDelete={() => setDeleting(tool)}
					/>
				))}
			</ul>
			<ConfirmDeleteDialog
				open={!!deleting}
				title={`Delete ${deleting?.name ?? 'custom tool'}?`}
				onOpenChange={(open) => !open && setDeleting(null)}
				onConfirm={() => void confirmDelete()}
			>
				Protocols that call it can no longer use it. This cannot be undone.
			</ConfirmDeleteDialog>
		</>
	);
};
